import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const loginUser = async (username, password) => {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('username', username.toLowerCase().trim())
    .eq('password', password)
    .single();
  if (error) return null;
  return data;
};

export const signUpUser = async (name, username, password, inviteCode, role = 'member', email = '') => {
  // Roles that require an invite code and which settings key to check
  const codeKeys = { member: 'invite_code', pt_client: 'pt_invite_code', expansion: 'expansion_invite_code' };
  const needsCode = role !== 'rooted';
  if (needsCode) {
    const validCode = await getSetting(codeKeys[role] || 'invite_code');
    if (!validCode || inviteCode.trim().toLowerCase() !== validCode.toLowerCase()) {
      return { error: 'Invalid invite code. Please contact Madison directly.' };
    }
  }
  const { data: existing } = await supabase.from('users').select('id').eq('username', username.toLowerCase().trim()).single();
  if (existing) return { error: 'That username is already taken — try another one.' };
  const idPrefix = { member: 'm', pt_client: 'pt', rooted: 'rooted', expansion: 'exp', belmondo_coach: 'blm' }[role] || 'm';
  const id = `${idPrefix}_${Date.now()}`;
  const { error } = await supabase.from('users').insert({
    id, name: name.trim(), role, username: username.toLowerCase().trim(), password,
    email: email.trim() || null, email_prompted: true,
  });
  if (error) return { error: 'Something went wrong — please try again.' };
  return { user: { id, name: name.trim(), role, username: username.toLowerCase().trim(), email: email.trim() || null, email_prompted: true } };
};

export const resetMemberPassword = async (userId, newPassword) => {
  const { error } = await supabase.from('users').update({ password: newPassword }).eq('id', userId);
  return !error;
};

// ─── Users ────────────────────────────────────────────────────────────────────
export const getMembers = async () => {
  const { data } = await supabase.from('users').select('*').eq('role', 'member');
  return data || [];
};

// ─── Check-ins ────────────────────────────────────────────────────────────────
// audience: 'bc' = only members (m_), 'pt' = only PT clients (pt_), null = all
export const getCheckins = async (userId = null, audience = null) => {
  let query = supabase.from('checkins').select('*').order('date', { ascending: false });
  if (userId) query = query.eq('user_id', userId);
  else if (audience === 'bc') query = query.like('user_id', 'm_%');
  else if (audience === 'pt') query = query.like('user_id', 'pt_%');
  const { data } = await query;
  return data || [];
};

export const saveCheckin = async (checkin) => {
  // Look for an existing row by user_id + date (handles any id format)
  const { data: existing } = await supabase
    .from('checkins')
    .select('id')
    .eq('user_id', checkin.userId)
    .eq('date', checkin.date)
    .maybeSingle();

  const payload = {
    user_id: checkin.userId,
    user_name: checkin.userName,
    date: checkin.date,
    checks: checkin.checks,
    mood: checkin.mood,
    energy: checkin.energy,
    notes: checkin.notes,
    custom_items: checkin.customItems,
  };

  let error;
  if (existing) {
    // Update the row that already exists (use its actual id)
    ({ error } = await supabase.from('checkins').update(payload).eq('id', existing.id));
  } else {
    // Insert a brand new row
    ({ error } = await supabase.from('checkins').insert({ id: checkin.id, ...payload }));
  }

  if (error) console.error('saveCheckin error:', error.message, error.details);
  return !error;
};

export const getTodayCheckin = async (userId) => {
  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
  const { data } = await supabase
    .from('checkins')
    .select('*')
    .eq('user_id', userId)
    .eq('date', today)
    .single();
  return data;
};

// ─── Workouts ─────────────────────────────────────────────────────────────────
export const getWorkouts = async (userId = null, audience = null) => {
  let query = supabase.from('workouts').select('*').order('date', { ascending: false });
  if (userId) query = query.eq('user_id', userId);
  else if (audience === 'bc') query = query.like('user_id', 'm_%');
  else if (audience === 'pt') query = query.like('user_id', 'pt_%');
  const { data } = await query;
  return data || [];
};

export const saveWorkout = async (workout) => {
  // For slot-based workouts, delete existing entry first then insert fresh
  if (workout.slot_key) {
    await supabase.from('workouts').delete().eq('id', workout.id);
  }
  const { error } = await supabase.from('workouts').insert({
    id: workout.id,
    user_id: workout.userId,
    user_name: workout.userName,
    date: workout.date,
    type: workout.type,
    notes: workout.notes,
    duration: workout.duration,
    slot_key: workout.slot_key || null,
  });
  return !error;
};

// ─── PRs ──────────────────────────────────────────────────────────────────────
export const getPRs = async (userId = null, audience = null) => {
  let query = supabase.from('prs').select('*').order('date', { ascending: false });
  if (userId) query = query.eq('user_id', userId);
  else if (audience === 'bc') query = query.like('user_id', 'm_%');
  const { data } = await query;
  return data || [];
};

export const savePR = async (pr) => {
  const { error } = await supabase.from('prs').insert({
    id: pr.id,
    user_id: pr.userId,
    user_name: pr.userName,
    exercise: pr.exercise,
    value: pr.value,
    unit: pr.unit,
    note: pr.note,
    date: pr.date,
  });
  return !error;
};

export const deletePR = async (id) => {
  const { error } = await supabase.from('prs').delete().eq('id', id);
  return !error;
};

// ─── Weight Logs ──────────────────────────────────────────────────────────────
export const getWeightLogs = async (userId) => {
  const { data } = await supabase
    .from('weight_logs')
    .select('*')
    .eq('user_id', userId)
    .order('date', { ascending: true });
  // Group by movement_key
  const grouped = {};
  (data || []).forEach(entry => {
    if (!grouped[entry.movement_key]) grouped[entry.movement_key] = [];
    grouped[entry.movement_key].push({ date: entry.date, weight: entry.weight, notes: entry.notes });
  });
  return grouped;
};

export const saveWeightLog = async (userId, movementKey, entry) => {
  const { error } = await supabase.from('weight_logs').insert({
    user_id: userId,
    movement_key: movementKey,
    weight: entry.weight,
    notes: entry.notes,
    date: entry.date,
  });
  return !error;
};

// ─── Nutrition Profile ────────────────────────────────────────────────────────
export const getNutritionProfile = async (userId) => {
  const { data } = await supabase
    .from('nutrition_profiles')
    .select('*')
    .eq('user_id', userId)
    .single();
  return data;
};

export const saveNutritionProfile = async (userId, profile) => {
  const { error } = await supabase.from('nutrition_profiles').upsert({
    user_id: userId,
    protein: profile.protein,
    carbs: profile.carbs,
    fat: profile.fat,
    veggies: profile.veggies,
    water: profile.water,
    allergies: profile.allergies,
    intolerances: profile.intolerances,
    goals: profile.goals,
    coach_notes: profile.coachNotes,
  }, { onConflict: 'user_id' });
  return !error;
};

// ─── Nutrition Logs ───────────────────────────────────────────────────────────
export const getNutritionLogs = async (userId) => {
  const { data } = await supabase
    .from('nutrition_logs')
    .select('*')
    .eq('user_id', userId)
    .order('date', { ascending: false });
  return data || [];
};

export const saveNutritionLog = async (userId, date, meals, photoUrls = {}) => {
  const { error } = await supabase.from('nutrition_logs').upsert({
    id: `${userId}_${date}`,
    user_id: userId,
    date,
    breakfast: meals.breakfast || '',
    snack1: meals.snack1 || '',
    lunch: meals.lunch || '',
    snack2: meals.snack2 || '',
    dinner: meals.dinner || '',
    photo_urls: photoUrls,
  }, { onConflict: 'id' });
  return !error;
};

export const uploadFoodPhoto = async (userId, file) => {
  const ext = file.name.split('.').pop() || 'jpg';
  const path = `${userId}/${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from('food-photos').upload(path, file, { upsert: true });
  if (error) return null;
  const { data } = supabase.storage.from('food-photos').getPublicUrl(path);
  return data.publicUrl;
};

// ─── Wins ─────────────────────────────────────────────────────────────────────
// audience: 'bc' = only members (m_), 'pt' = only PT clients (pt_), null = all
export const getWins = async (audience = null) => {
  let query = supabase.from('wins').select('*').order('created_at', { ascending: false });
  if (audience === 'bc') query = query.like('user_id', 'm_%');
  else if (audience === 'pt') query = query.like('user_id', 'pt_%');
  const { data } = await query;
  return data || [];
};

export const saveWin = async (win) => {
  const { error } = await supabase.from('wins').insert({
    id: win.id,
    user_id: win.userId,
    user_name: win.userName,
    type: win.type,
    text: win.text,
    url: win.url || '',
    date: win.date,
    likes: [],
    comments: [],
  });
  return !error;
};

export const toggleLike = async (table, id, userId) => {
  const { data } = await supabase.from(table).select('likes').eq('id', id).single();
  if (!data) return;
  const likes = data.likes || [];
  const newLikes = likes.includes(userId)
    ? likes.filter(l => l !== userId)
    : [...likes, userId];
  await supabase.from(table).update({ likes: newLikes }).eq('id', id);
};

export const addComment = async (table, id, userId, userName, text) => {
  const { data } = await supabase.from(table).select('comments').eq('id', id).single();
  if (!data) return;
  const comments = data.comments || [];
  comments.push({ userId, userName, text, date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })() });
  await supabase.from(table).update({ comments }).eq('id', id);
};

export const deleteWin = async (id) => {
  await supabase.from('wins').delete().eq('id', id);
};

// ─── Q&A ──────────────────────────────────────────────────────────────────────
export const getQA = async () => {
  const { data } = await supabase
    .from('qa')
    .select('*')
    .order('created_at', { ascending: false });
  return data || [];
};

export const saveQuestion = async (q) => {
  const { error } = await supabase.from('qa').insert({
    id: q.id,
    user_id: q.userId,
    user_name: q.userName,
    question: q.question,
    is_anon: q.isAnon,
    date: q.date,
    likes: [],
    comments: [],
  });
  return !error;
};

export const saveAnswer = async (id, answer) => {
  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
  await supabase.from('qa').update({ answer, answered_date: today }).eq('id', id);
};

export const deleteQA = async (id) => {
  await supabase.from('qa').delete().eq('id', id);
};

// ─── Announcements ────────────────────────────────────────────────────────────
export const getAnnouncements = async (audience = 'bc', recipientId = null) => {
  let query = supabase.from('announcements').select('*').eq('audience', audience).order('created_at', { ascending: false });
  if (recipientId) query = query.eq('recipient_id', recipientId);
  const { data } = await query;
  return data || [];
};

export const saveAnnouncement = async (ann) => {
  const { error } = await supabase.from('announcements').insert({
    id: ann.id,
    title: ann.title,
    body: ann.body,
    date: ann.date,
    audience: ann.audience || 'bc',
    recipient_id: ann.recipient_id || null,
  });
  return !error;
};

export const deleteAnnouncement = async (id) => {
  await supabase.from('announcements').delete().eq('id', id);
};

// ─── Weekly Workouts ──────────────────────────────────────────────────────────
export const getWeeklyWorkouts = async () => {
  const { data } = await supabase
    .from('weekly_workouts')
    .select('*')
    .order('created_at', { ascending: false });
  return data || [];
};

export const saveWeeklyWorkout = async (ww) => {
  const { error } = await supabase.from('weekly_workouts').insert({
    id: ww.id,
    week: ww.week,
    slots: ww.slots,
    posted_by: ww.posted_by || ww.postedBy,
    posted_date: ww.posted_date || ww.postedDate,
  });
  return !error;
};

// ─── Settings (flyer, if/then) ────────────────────────────────────────────────
export const getSetting = async (key) => {
  const { data } = await supabase.from('settings').select('value').eq('key', key).single();
  return data?.value || null;
};

export const saveSetting = async (key, value) => {
  await supabase.from('settings').upsert({ key, value }, { onConflict: 'key' });
};

// ─── Video Library ────────────────────────────────────────────────────────────
export const getVideoLibrary = async () => {
  const { data } = await supabase.from('video_library').select('*').order('exercise', { ascending: true });
  return data || [];
};

export const saveVideo = async (video) => {
  const { error } = await supabase.from('video_library').insert({
    id: video.id,
    title: video.title,
    exercise: video.exercise,
    youtube_url: video.youtubeUrl,
  });
  return !error;
};

export const deleteVideo = async (id) => {
  await supabase.from('video_library').delete().eq('id', id);
};

export const updateWeeklyWorkout = async (id, data) => {
  const { error } = await supabase.from('weekly_workouts').update({
    week: data.week,
    slots: data.slots,
  }).eq('id', id);
  return !error;
};

// ─── Kudos ────────────────────────────────────────────────────────────────────
export const getKudos = async (userId = null) => {
  let query = supabase.from('kudos').select('*').order('created_at', { ascending: false });
  if (userId) query = query.eq('to_user_id', userId);
  const { data } = await query;
  return data || [];
};

export const sendKudos = async (kudo) => {
  const { error } = await supabase.from('kudos').insert({
    id: kudo.id,
    from_user_id: kudo.fromUserId,
    from_user_name: kudo.fromUserName,
    to_user_id: kudo.toUserId,
    to_user_name: kudo.toUserName,
    message: kudo.message,
  });
  return !error;
};

// ─── PT (Personal Training) ───────────────────────────────────────────────────
export const getPTClients = async () => {
  const { data } = await supabase.from('users').select('*').eq('role', 'pt_client');
  return data || [];
};

export const getPTCheckItems = async (clientId) => {
  const { data } = await supabase.from('pt_check_items').select('*').eq('client_id', clientId);
  return data || [];
};
export const savePTCheckItem = async (item) => {
  const { error } = await supabase.from('pt_check_items').insert({ id: item.id, client_id: item.clientId, label: item.label });
  return !error;
};
export const deletePTCheckItem = async (id) => {
  await supabase.from('pt_check_items').delete().eq('id', id);
};

export const getPTPrograms = async (clientId) => {
  const { data } = await supabase.from('pt_programs').select('*').eq('client_id', clientId).order('created_at', { ascending: false });
  return data || [];
};
export const savePTProgram = async (program) => {
  const { error } = await supabase.from('pt_programs').insert({
    id: program.id, client_id: program.clientId, week: program.week,
    slots: program.slots, posted_date: program.postedDate,
  });
  return !error;
};
export const updatePTProgram = async (id, data) => {
  const { error } = await supabase.from('pt_programs').update({ week: data.week, slots: data.slots }).eq('id', id);
  return !error;
};
export const deletePTProgram = async (id) => {
  await supabase.from('pt_programs').delete().eq('id', id);
};

export const getPTGoals = async (clientId) => {
  const { data } = await supabase.from('pt_goals').select('*').eq('client_id', clientId).order('created_at', { ascending: false });
  return data || [];
};
export const savePTGoal = async (goal) => {
  const { error } = await supabase.from('pt_goals').insert({ id: goal.id, client_id: goal.clientId, goal_text: goal.goalText });
  return !error;
};
export const updatePTGoal = async (id, fields) => {
  const payload = typeof fields === 'string' ? { goal_text: fields } : fields;
  const { error } = await supabase.from('pt_goals').update({ ...payload, updated_at: new Date().toISOString() }).eq('id', id);
  return !error;
};
export const deletePTGoal = async (id) => {
  await supabase.from('pt_goals').delete().eq('id', id);
};

export const getPTAppointments = async (clientId) => {
  const { data } = await supabase.from('pt_appointments').select('*').eq('client_id', clientId).order('date');
  return data || [];
};
export const savePTAppointment = async (appt) => {
  const { error } = await supabase.from('pt_appointments').insert({
    id: appt.id, client_id: appt.clientId, date: appt.date, time: appt.time || '', notes: appt.notes || '',
  });
  return !error;
};
export const deletePTAppointment = async (id) => {
  await supabase.from('pt_appointments').delete().eq('id', id);
};

export const getPTReactions = async (clientId) => {
  const { data } = await supabase.from('pt_reactions').select('*').eq('client_id', clientId).order('created_at', { ascending: false });
  return data || [];
};
export const savePTReaction = async (reaction) => {
  const { error } = await supabase.from('pt_reactions').insert({
    id: reaction.id, coach_id: reaction.coachId, client_id: reaction.clientId,
    target_type: reaction.targetType, target_id: reaction.targetId,
    reaction_type: reaction.reactionType, comment_text: reaction.commentText || '',
  });
  return !error;
};
export const markPTReactionsSeen = async (clientId) => {
  await supabase.from('pt_reactions').update({ seen: true }).eq('client_id', clientId).eq('seen', false);
};

// ─── Attendance ───────────────────────────────────────────────────────────────
export const getAttendance = async () => {
  const { data } = await supabase.from('attendance').select('*').order('date', { ascending: false });
  return data || [];
};

export const saveAttendance = async (date, presentIds) => {
  const { error } = await supabase.from('attendance').upsert(
    { date, present_ids: presentIds },
    { onConflict: 'date' }
  );
  return !error;
};

// ─── Messaging ────────────────────────────────────────────────────────────────
export const getMessages = async (conversationId) => {
  const { data } = await supabase
    .from('messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });
  return data || [];
};

export const sendMessage = async (msg) => {
  const { error } = await supabase.from('messages').insert({
    id: msg.id,
    conversation_id: msg.conversationId,
    sender_id: msg.senderId,
    sender_name: msg.senderName,
    body: msg.body,
  });
  return !error;
};

export const getConversationPreviews = async (userId) => {
  const { data } = await supabase
    .from('messages')
    .select('*')
    .or(`conversation_id.like.%${userId}%,conversation_id.eq.bc_group`)
    .order('created_at', { ascending: false });
  if (!data) return [];
  const seen = new Set();
  return data.filter(m => { if (seen.has(m.conversation_id)) return false; seen.add(m.conversation_id); return true; });
};

// ─── Email / Profile ──────────────────────────────────────────────────────────
export const saveUserEmail = async (userId, email) => {
  const { error } = await supabase.from('users').update({ email: email.trim(), email_prompted: true }).eq('id', userId);
  return !error;
};

export const getBelmondoMembers = async () => {
  const { data } = await supabase.from('users').select('*').in('role', ['rooted', 'expansion', 'pt_client']);
  return data || [];
};

// ─── User Preferences ────────────────────────────────────────────────────────
export const getUserPrefs = async (userId) => {
  const { data } = await supabase.from('user_preferences').select('*').eq('user_id', userId).maybeSingle();
  return data || {};
};
export const saveUserPrefs = async (userId, prefs) => {
  const { data: existing } = await supabase.from('user_preferences').select('user_id').eq('user_id', userId).maybeSingle();
  if (existing) {
    const { error } = await supabase.from('user_preferences').update(prefs).eq('user_id', userId);
    return !error;
  } else {
    const { error } = await supabase.from('user_preferences').insert({ user_id: userId, ...prefs });
    return !error;
  }
};

// ─── Journal Entries ──────────────────────────────────────────────────────────
export const getJournalEntry = async (userId, date) => {
  const { data } = await supabase.from('journal_entries').select('*').eq('user_id', userId).eq('date', date).maybeSingle();
  return data || null;
};
export const saveJournalEntry = async (userId, date, fields) => {
  const id = `je_${userId}_${date}`;
  const { data: existing } = await supabase.from('journal_entries').select('id').eq('id', id).maybeSingle();
  if (existing) {
    const { error } = await supabase.from('journal_entries').update(fields).eq('id', id);
    return !error;
  } else {
    const { error } = await supabase.from('journal_entries').insert({ id, user_id: userId, date, ...fields });
    return !error;
  }
};

export const getAllJournalEntriesForUsers = async (userIds) => {
  if (!userIds.length) return [];
  const { data } = await supabase.from('journal_entries').select('*').in('user_id', userIds);
  return data || [];
};

export const getAllCupFillersForUsers = async (userIds) => {
  if (!userIds.length) return [];
  const { data } = await supabase.from('cup_fillers').select('*').in('user_id', userIds);
  return data || [];
};

// ─── Cup Fillers ──────────────────────────────────────────────────────────────
export const getCupFillers = async (userId) => {
  const { data } = await supabase.from('cup_fillers').select('*').eq('user_id', userId).order('sort_order');
  return data || [];
};
export const saveCupFiller = async (filler) => {
  const { error } = await supabase.from('cup_fillers').insert({ id: filler.id, user_id: filler.userId, label: filler.label, is_checked: false, sort_order: filler.sortOrder || 0 });
  return !error;
};
export const updateCupFiller = async (id, fields) => {
  const { error } = await supabase.from('cup_fillers').update(fields).eq('id', id);
  return !error;
};
export const deleteCupFiller = async (id) => {
  await supabase.from('cup_fillers').delete().eq('id', id);
};

// ─── Community Posts ──────────────────────────────────────────────────────────
// programs: 'rooted' | 'expansion' | 'shared' (wins+gratitudes visible to both)
export const getCommunityPosts = async (programs = ['rooted']) => {
  const { data } = await supabase.from('community_posts').select('*').in('program', programs).order('created_at', { ascending: false });
  return data || [];
};
export const saveCommunityPost = async (post) => {
  const { error } = await supabase.from('community_posts').insert({
    id: post.id, user_id: post.userId, user_name: post.userName,
    program: post.program, type: post.type, content: post.content,
    image_url: post.imageUrl || null, link_url: post.linkUrl || null,
  });
  return !error;
};
export const deleteCommunityPost = async (id) => {
  await supabase.from('community_posts').delete().eq('id', id);
};
export const getCommunityHearts = async (postIds) => {
  if (!postIds.length) return [];
  const { data } = await supabase.from('community_hearts').select('*').in('post_id', postIds);
  return data || [];
};
export const toggleCommunityHeart = async (postId, userId) => {
  const { data: existing } = await supabase.from('community_hearts').select('id').eq('post_id', postId).eq('user_id', userId).maybeSingle();
  if (existing) {
    await supabase.from('community_hearts').delete().eq('id', existing.id);
    return 'removed';
  } else {
    await supabase.from('community_hearts').insert({ id: `heart_${postId}_${userId}`, post_id: postId, user_id: userId });
    return 'added';
  }
};
export const getCommunityComments = async (postId) => {
  const { data } = await supabase.from('community_comments').select('*').eq('post_id', postId).order('created_at');
  return data || [];
};
export const saveCommunityComment = async (comment) => {
  const { error } = await supabase.from('community_comments').insert({
    id: comment.id, post_id: comment.postId, user_id: comment.userId, user_name: comment.userName, content: comment.content,
  });
  return !error;
};

// ─── Program Resources (coach-managed) ───────────────────────────────────────
export const getProgramResources = async (program, type = null) => {
  let q = supabase.from('program_resources').select('*').eq('program', program).order('sort_order');
  if (type) q = q.eq('type', type);
  const { data } = await q;
  return data || [];
};
export const saveProgramResource = async (res) => {
  const { error } = await supabase.from('program_resources').insert({
    id: res.id, program: res.program, type: res.type, title: res.title,
    content: res.content || null, url: res.url || null, week_number: res.weekNumber || null, sort_order: res.sortOrder || 0,
  });
  return !error;
};
export const deleteProgramResource = async (id) => {
  await supabase.from('program_resources').delete().eq('id', id);
};

// ─── Expansion Workouts ───────────────────────────────────────────────────────
export const getExpansionWorkouts = async (track, weekNumber) => {
  const { data } = await supabase.from('expansion_workouts').select('*').eq('track', track).eq('week_number', weekNumber).order('day_number');
  return data || [];
};
export const saveExpansionWorkout = async (workout) => {
  const { error } = await supabase.from('expansion_workouts').insert({
    id: workout.id, track: workout.track, week_number: workout.weekNumber,
    day_number: workout.dayNumber, title: workout.title, exercises: workout.exercises,
  });
  return !error;
};
export const deleteExpansionWorkout = async (id) => {
  await supabase.from('expansion_workouts').delete().eq('id', id);
};
export const getAllExpansionWorkouts = async () => {
  const { data } = await supabase.from('expansion_workouts').select('*').order('week_number').order('day_number');
  return data || [];
};

// ─── Workout Logs ─────────────────────────────────────────────────────────────
export const getWorkoutLog = async (userId, workoutId) => {
  const { data } = await supabase.from('expansion_workout_logs').select('*').eq('user_id', userId).eq('workout_id', workoutId).maybeSingle();
  return data || null;
};
export const saveWorkoutLog = async (userId, workoutId, fields) => {
  const id = `wl_${userId}_${workoutId}`;
  const { data: existing } = await supabase.from('expansion_workout_logs').select('id').eq('id', id).maybeSingle();
  if (existing) {
    const { error } = await supabase.from('expansion_workout_logs').update(fields).eq('id', id);
    return !error;
  } else {
    const { error } = await supabase.from('expansion_workout_logs').insert({ id, user_id: userId, workout_id: workoutId, ...fields });
    return !error;
  }
};
export const getUserWorkoutLogs = async (userId) => {
  const { data } = await supabase.from('expansion_workout_logs').select('*').eq('user_id', userId);
  return data || [];
};

// ─── Movement Library ─────────────────────────────────────────────────────────
export const getMovementLibrary = async () => {
  const { data } = await supabase.from('movement_library').select('*').order('name');
  return data || [];
};
export const saveMovement = async (movement) => {
  const { error } = await supabase.from('movement_library').insert({
    id: movement.id, name: movement.name, description: movement.description,
    video_url: movement.videoUrl || null, track: movement.track || 'both',
  });
  return !error;
};
export const deleteMovement = async (id) => {
  await supabase.from('movement_library').delete().eq('id', id);
};

// ─── PR Tracking (Expansion) ──────────────────────────────────────────────────
export const getExpansionPRs = async (userId) => {
  const { data } = await supabase.from('expansion_prs').select('*').eq('user_id', userId).order('logged_at', { ascending: false });
  return data || [];
};
export const saveExpansionPR = async (pr) => {
  const { error } = await supabase.from('expansion_prs').insert({
    id: pr.id, user_id: pr.userId, movement_name: pr.movementName, value: pr.value, unit: pr.unit,
  });
  return !error;
};
export const deleteExpansionPR = async (id) => {
  await supabase.from('expansion_prs').delete().eq('id', id);
};
export const getProgramStartDate = async () => {
  const { data } = await supabase.from('app_settings').select('value').eq('key', 'expansion_start_date').maybeSingle();
  return data?.value || null;
};
export const saveProgramStartDate = async (date) => {
  const { data: existing } = await supabase.from('app_settings').select('key').eq('key', 'expansion_start_date').maybeSingle();
  if (existing) {
    await supabase.from('app_settings').update({ value: date }).eq('key', 'expansion_start_date');
  } else {
    await supabase.from('app_settings').insert({ key: 'expansion_start_date', value: date });
  }
};

// ─── Community Image Upload ───────────────────────────────────────────────────
export const uploadCommunityImage = async (file, userId) => {
  const ext = file.name.split('.').pop();
  const path = `community/${userId}_${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from('community-images').upload(path, file, { upsert: true });
  if (error) return null;
  const { data } = supabase.storage.from('community-images').getPublicUrl(path);
  return data?.publicUrl || null;
};

// ─── Push Notifications ───────────────────────────────────────────────────────
const VAPID_PUBLIC_KEY = 'BByb8djc33cB_dnyawcYtB0gYs_eQvsh9zoPsKbob-q6ctQKdS7nyG2uXWUEvNlGqwGzdQXGnnEW4ZMiN1rXlDQ';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return new Uint8Array([...raw].map(c => c.charCodeAt(0)));
}

export const registerPushSubscription = async (userId) => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
    const { data: existing } = await supabase.from('push_subscriptions').select('id').eq('user_id', userId).maybeSingle();
    if (existing) {
      await supabase.from('push_subscriptions').update({ subscription: sub.toJSON() }).eq('user_id', userId);
    } else {
      await supabase.from('push_subscriptions').insert({ user_id: userId, subscription: sub.toJSON() });
    }
    return true;
  } catch (e) {
    return false;
  }
};

export const sendPushNotification = async ({ title, body, userIds }) => {
  const res = await fetch('/api/send-push', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title, body, userIds }),
  });
  return res.ok ? res.json() : null;
};

export const scheduleNotification = async ({ title, body, audience, sendAt }) => {
  const { data, error } = await supabase.from('scheduled_notifications').insert({
    title, body, audience, send_at: sendAt, sent: false,
  }).select().single();
  return error ? null : data;
};

export const getScheduledNotifications = async () => {
  const { data } = await supabase
    .from('scheduled_notifications')
    .select('*')
    .eq('sent', false)
    .order('send_at', { ascending: true });
  return data || [];
};

export const deleteScheduledNotification = async (id) => {
  await supabase.from('scheduled_notifications').delete().eq('id', id);
};
